import React, { useState } from "react";
import PropTypes from "prop-types";
import DatePicker, { registerLocale } from "react-datepicker";
import { InputMask } from "primereact/inputmask";
import { format } from "date-fns";

import "react-datepicker/dist/react-datepicker.css";
import ptBR from "date-fns/locale/pt-BR";

import { validateDate, applyDateMask, parseDate } from "utils/DateValidationUtils";

registerLocale("pt-BR", ptBR);

const InputDate = ({
  value,
  onChange,
  placeholder = "dd/mm/aaaa",
  maxDate = null,
  showMonthDropdown = true,
  showYearDropdown = true,
  dropdownMode = "select",
  className = "form-control",
  disabled = false,
  required = false,
  id,
  name,
  label
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dataError, setDataError] = useState("");

  const handleInputChange = (e) => {
    const { value: inputValue } = e.target;
    
    // Se o campo foi limpo (está vazio ou só tem underscores), notificar o onChange
    if (!inputValue || inputValue === "" || inputValue === "__/__/____") {
      onChange("");
      return;
    }
    
    if (
      inputValue &&
      inputValue.length === 10 &&
      inputValue.includes('/') &&
      !inputValue.includes('_')
    ) {
      // Valida se é uma data válida
      const date = parseDate(inputValue);
      if (date) {
        onChange(inputValue);
      }
      return;
    }
    
    const masked = applyDateMask(inputValue);
    
    // Converte para string se for uma data válida
    if (masked.length === 10) {
      const date = parseDate(masked);
      if (date) {
        // Ajusta para evitar problemas de timezone
        const adjustedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const formatted = format(adjustedDate, "dd/MM/yyyy");
        onChange(formatted);
      }
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      // Ajusta para evitar problemas de timezone
      const adjustedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const formatted = format(adjustedDate, "dd/MM/yyyy");
      onChange(formatted);
    } else {
      onChange("");
    }
    setShowDatePicker(false);
  };

  const handleBlur = (e) => {
    const { value: inputValue } = e.target;
    // Só valida se o usuário digitou algo novo
    if (inputValue && inputValue !== value) {
      if (!validateDate(inputValue, maxDate)) {
        setDataError("Data inválida");
        onChange("");
        e.target.value = "";
      } else {
        setDataError("");
      }
    } else {
      setDataError("");
    }
  };

  // Fechar datepicker quando clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.position-relative')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  const displayValue = value || "";
  


  return (
    <div className="position-relative">
      <InputMask
        id={id}
        name={name}
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        mask="99/99/9999"
        placeholder={placeholder}
        className={`${className} ${dataError ? 'is-invalid' : ''}`}
        onFocus={() => !disabled && setShowDatePicker(true)}
        disabled={disabled}
        required={required}
        key={value} // Força re-render quando o valor muda
      />
      {dataError && (
        <div className="invalid-feedback">
          {dataError}
        </div>
      )}
      {showDatePicker && !disabled && (
        <div className="position-absolute" style={{ zIndex: 1000, top: '100%', left: 0 }}>
          <DatePicker
            selected={(() => {
              if (!value) return null;
              const parsed = parseDate(value);
              return parsed;
            })()}
            onChange={handleDateChange}
            dateFormat="dd/MM/yyyy"
            locale="pt-BR"
            showMonthDropdown={showMonthDropdown}
            showYearDropdown={showYearDropdown}
            dropdownMode={dropdownMode}
            maxDate={maxDate}
            inline
          />
        </div>
      )}
    </div>
  );
};

InputDate.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  maxDate: PropTypes.instanceOf(Date),
  showMonthDropdown: PropTypes.bool,
  showYearDropdown: PropTypes.bool,
  dropdownMode: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string
};

InputDate.defaultProps = {
  value: "",
  placeholder: "dd/mm/aaaa",
  maxDate: null,
  showMonthDropdown: true,
  showYearDropdown: true,
  dropdownMode: "select",
  className: "form-control",
  disabled: false,
  required: false,
  id: undefined,
  name: undefined,
  label: undefined
};

export default InputDate; 